// MainDashboard — Sprint 14 orchestrator. Now a thin layout shell
// (~270 LOC, down from 757). Three big chunks moved out:
//
//   1. Data layer  → hooks/useDashboardData (heavy initial-load
//                    effect + condo-switch flow + stats state).
//   2. Menu state  → hooks/useMenuState (selected page + expanded
//                    groups + role/feature-filtered item list).
//   3. Logout      → hooks/useLogout (the security-critical path
//                    with the C1 hotfix story documented next to
//                    the code).
//
// Five inline modals (~270 LOC) moved to components/dashboard/modals.
// The page-header strip + footer became their own components.
import React, { useEffect, useState } from 'react';
// Sprint 49 — F48 mobile-friendly baseline + PWA. Loaded once at
// the dashboard root so the responsive rules apply across every
// page without per-page opt-in.
import './styles/responsive.css';
import { ConfigProvider, Form, Layout, Spin, Typography, type MenuProps as AntdMenuProps } from 'antd';
import { staticApi } from './utils/staticApi';
import {
  LogoutOutlined, ProfileOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import { useAuthStore } from './store/authStore';
import { useCondoStore } from './store/condoStore';
import { useAuth } from '../../context/UnifiedAuthContext';

import { DashboardHeader }    from './components/dashboard/DashboardHeader';
import { DashboardSidebar }   from './components/dashboard/DashboardSidebar';
import { DashboardRouter }    from './components/dashboard/DashboardRouter';
import { RouteErrorBoundary } from './components/dashboard/RouteErrorBoundary';
import { RouteNotFound, RouteForbidden } from './components/dashboard/RouteNotFound';
import { DashboardPageHeader } from './components/dashboard/DashboardPageHeader';
import { DashboardFooter }     from './components/dashboard/DashboardFooter';
import {
  ALL_MENU_ITEMS, MENU_GROUPS, GROUP_COLORS,
} from './components/dashboard/menuConfig';
import { useDashboardData } from './components/dashboard/hooks/useDashboardData';
import { useMenuState }     from './components/dashboard/hooks/useMenuState';
import { useLogout }        from './components/dashboard/hooks/useLogout';
// Sprint 82 (ADR-082) — Atrium two-tier shell, gated behind a client flag.
// Renders INSTEAD of the legacy Layout/Sidebar when on; same DashboardRouter
// content + same selected-page state. Additive + reversible.
import { useAtriumRedesign } from './hooks/useAtriumRedesign';
import { atriumAntdTheme }   from '../../theme/theme';
import { AtriumShell }       from './components/atrium/AtriumShell';
import { AtriumNavProvider } from './components/atrium/atriumNav';
// Sprint 89B (M3) — modal theming is now handled at the GLOBAL ConfigProvider in
// App.tsx (flag-aware: atriumAntdTheme when the flag is on), which is the
// outermost AntD provider and therefore the guaranteed ancestor of every
// portaled <Modal>. The Sprint 88B body-class CSS override (atrium-portals.css)
// lost to AntD's CSS-in-JS token specificity and was removed.
import { ProfileModal }       from './components/dashboard/modals/ProfileModal';
// Sprint 144 (R14-5/R14-6, ADR-139) — SettingsModal was DELETED, not repaired.
// Every control in it was client-only: the notification toggles, dark mode,
// language and timezone had no endpoint behind them, "Change Password" and the
// 2FA switch had no handler at all, and the "Active Sessions" list was a
// hardcoded fixture naming a Safari-on-iPhone session in Miami that never
// existed. Fabricated security telemetry is worse than none, and there is no
// session-listing endpoint to build a real one against — so the surface is
// removed rather than faked. Its two entry points (the avatar-menu item and the
// NavRail gear) went with it.
import { LogoutConfirmModal } from './components/dashboard/modals/LogoutConfirmModal';
import { QuickInsightsModal } from './components/dashboard/modals/QuickInsightsModal';
import { ExportReportModal }  from './components/dashboard/modals/ExportReportModal';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Content } = Layout;

export const MainDashboard: React.FC = () => {
  const { owners, fees, getRecentPayments } = useCondoStore();
  const navigate = useNavigate();
  const {
    currentCondo, availableCondos,
    fetchAvailableCondos, featureSettings,
    user: condoUser, updateMyProfile,
  } = useAuthStore();
  const { user: unifiedUser, isAuthenticated, logout } = useAuth();
  void fetchAvailableCondos; // referenced in useDashboardData; keep destructure stable

  // Sprint 144 — a profile save lands in the condo authStore (the real
  // PUT /api/auth/user/{id}); the UnifiedAuthContext copy of the user is not
  // refetched, so the header would keep rendering the pre-save name until a
  // reload. Overlay the store's live identity fields for display. Same session,
  // same person — the store user IS the condo user this shell is bound to.
  const user = React.useMemo(
    () => (unifiedUser && condoUser
      ? { ...unifiedUser, name: condoUser.name, email: condoUser.email, phone: (condoUser as any).phone }
      : unifiedUser),
    [unifiedUser, condoUser],
  ) as typeof unifiedUser;

  // Sidebar collapse toggle — pure UI state, lives next to the
  // header/sidebar that drive it.
  const [collapsed, setCollapsed] = useState(false);

  // Forms must live in the orchestrator (next to the modal-open state
  // they pair with); the modal components consume them as props.
  // Sprint 144 — the profile form is now prefilled at OPEN time from the live
  // store user (`openProfile`), not once inside useDashboardData's load effect.
  // That effect ran before any save and seeded a hardcoded phone (R14-28), so
  // the modal could show stale/invented values.
  const [profileForm]  = Form.useForm();
  const [exportForm]   = Form.useForm();

  // ── Data layer ──────────────────────────────────────────────────────
  const { loading, stats, switchTo } = useDashboardData();

  // ── Menu state ──────────────────────────────────────────────────────
  // Sprint R11-8 (ADR-128) — `selected` is derived from the URL; routeStatus
  // tells us whether the URL named a real page this user may see.
  const {
    selected, expandedGroups, toggleGroup, filteredItems, selectAndReveal,
    routeStatus, routeSlug,
  } = useMenuState({ user: user as any, featureSettings });

  // Sprint R11-8 (ADR-128) — this bounce used to run during render
  // (`if (!isAuthenticated) navigate(...)`), which mutates the router mid-
  // commit. CondoProtectedRoute is the real guard; this is the belt-and-braces
  // case where auth drops while the dashboard is already mounted.
  useEffect(() => {
    if (!loading && !isAuthenticated) navigate('/login-condo-app/', { replace: true });
  }, [loading, isAuthenticated, navigate]);

  // ── Atrium shell flag (Sprint 82) ───────────────────────────────────
  const atriumOn = useAtriumRedesign();

  // ── Modal visibility ────────────────────────────────────────────────
  const [profileOpen,        setProfileOpen]        = useState(false);
  const [logoutOpen,         setLogoutOpen]         = useState(false);
  const [userDropdownOpen,   setUserDropdownOpen]   = useState(false);
  const [quickInsightsOpen,  setQuickInsightsOpen]  = useState(false);
  const [exportReportOpen,   setExportReportOpen]   = useState(false);

  const [profileSaving, setProfileSaving] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // ── Logout ──────────────────────────────────────────────────────────
  const performLogout = useLogout({
    unifiedLogout:  logout,
    onBeforeLogout: () => {
      setLogoutOpen(false);
      setUserDropdownOpen(false);
    },
  });

  // ── Modal submit handlers ───────────────────────────────────────────
  // Sprint 144 (R14-5/R14-28, ADR-139) — this used to be
  // `await new Promise(r => setTimeout(r, 1000))` followed by a success toast,
  // with ZERO network calls: the modal claimed the save and discarded it. It
  // now goes through the SAME self-scoped store action the working My Home
  // "Edit profile" path uses (`updateMyProfile` → PUT /api/auth/user/{id}) —
  // reusing the real implementation rather than growing a second one. Strictly
  // self-scoped: the form carries no user id; the action always targets the
  // logged-in user, and the backend enforces self-scope independently.
  const openProfile = () => {
    profileForm.setFieldsValue({
      name:  condoUser?.name  ?? user?.name  ?? '',
      email: condoUser?.email ?? user?.email ?? '',
      // R14-28 — was hardcoded '+1 (555) 123-4567'. Real value or empty.
      phone: (condoUser as any)?.phone ?? '',
    });
    setProfileOpen(true);
  };

  const handleProfileUpdate = async () => {
    let values: { name?: string; email?: string; phone?: string };
    try {
      values = await profileForm.validateFields();
    } catch {
      return; // inline field errors are enough
    }
    // `updateMyProfile` is a no-op when the condo store has no user (it needs
    // the caller's own id to build the URL). Without this guard the modal would
    // toast success having sent nothing — reintroducing the exact bug this
    // sprint is closing. Fail loudly instead.
    if (!condoUser) {
      staticApi.message.error('Your session is still loading — please try again in a moment.');
      return;
    }
    setProfileSaving(true);
    try {
      await updateMyProfile({
        name:        values.name?.trim()  || undefined,
        email:       values.email?.trim() || undefined,
        phoneNumber: values.phone?.trim() ?? undefined,
      });
      staticApi.message.success('Profile updated');
      setProfileOpen(false);
    } catch (err: any) {
      staticApi.message.error(
        err?.message || 'Could not update your profile. Please try again.',
      );
    } finally {
      setProfileSaving(false);
    }
  };

  const handleExportReport = async () => {
try {
      setExportLoading(true);
      await new Promise(resolve => setTimeout(resolve, 1500));
      staticApi.message.success({ content: 'Report exported successfully', duration: 3 });
      setExportReportOpen(false);
      exportForm.resetFields();

      const link = document.createElement('a');
      link.href = '#';
      // Sprint 145 (R14-9) — a report exported at 21:00 EDT on the 19th was
      // named for the 20th. The filename is how an operator files it; it must
      // agree with the day they exported it.
      link.download = `condo-report-${dayjs().format('YYYY-MM-DD')}.pdf`;
      link.click();
    } catch {
      staticApi.message.error('Failed to export report');
    } finally {
      setExportLoading(false);
    }
  };

  // ── User dropdown items ─────────────────────────────────────────────
  const userMenuItems: AntdMenuProps['items'] = [
    { key: 'profile',  icon: <ProfileOutlined />, label: 'My Profile',       onClick: openProfile },
    // Sprint 144 — 'Account Settings' removed with the modal it opened. See the
    // SettingsModal import-site note above.
    { key: 'divider',  type: 'divider' },
    // C1 hotfix — go direct to performLogout. See useLogout.ts docstring.
    { key: 'logout',   icon: <LogoutOutlined />,  label: 'Sign Out',         onClick: () => performLogout()       },
  ];

  // ── Derived data ────────────────────────────────────────────────────
  const recentPayments = getRecentPayments(5, currentCondo?.id);
  // Sprint 32 — the `!currentCondo ||` branch made overdueFees fall
  // back to the FULL store (e.g. 11 across both seeded condos), which
  // then surfaced as a "11" notification badge on every dashboard
  // header — even for admin without a condo binding and for owner
  // before currentCondo resolved. Same shape as the Sprint 30 F15 bug
  // on workOrders/violations: require an actual condo match before
  // counting anything.
  const overdueFees    = fees.filter(fee =>
    fee.status === 'OVERDUE' && currentCondo != null && fee.condoId === currentCondo.id,
  );
  const pageTitle = (ALL_MENU_ITEMS.find(i => i.key === selected)?.label) || 'Dashboard';

  // ── Loading / auth guards ───────────────────────────────────────────
  if (loading) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)',
      }}>
        <div style={{ textAlign: 'center' }}>
          <Spin size="large" style={{ marginBottom: 16 }} />
          <Title level={4} style={{ color: '#1890ff', margin: 0 }}>Loading Dashboard</Title>
          <Text type="secondary">Preparing your workspace...</Text>
        </div>
      </div>
    );
  }

  // ── Shared content area (same for both shells) ──────────────────────
  // Sprint R11-8 (ADR-128) — an unknown or gated URL renders an honest Result
  // instead of silently falling through to the role's home page. `resolving`
  // is the bare `/condo-app/` path: useMenuState is redirecting to the role
  // default, so render nothing rather than flash the wrong page.
  const routerContent =
    routeStatus === 'resolving' ? null
    : routeStatus === 'not-found' ? (
      <RouteNotFound slug={routeSlug} homeKey={selected} onGoHome={selectAndReveal} />
    ) : routeStatus === 'forbidden' ? (
      <RouteForbidden
        label={ALL_MENU_ITEMS.find(i => i.key === routeSlug)?.label ?? 'That page'}
        homeKey={selected}
        onGoHome={selectAndReveal}
      />
    ) : (
      <RouteErrorBoundary routeKey={selected}>
        <DashboardRouter
          selected={selected}
          userRole={user?.role}
          overview={{ stats, recentPayments, overdueFees, owners, onNavigate: selectAndReveal }}
        />
      </RouteErrorBoundary>
    );

  // ── Atrium two-tier shell (Sprint 82, flag-gated) ───────────────────
  // Renders INSTEAD of the legacy Layout/Sidebar. Reuses the same filtered
  // menu items, selected-page state, and DashboardRouter — no duplicated
  // routing or fetch logic. Modals are shared below.
  if (atriumOn) {
    const avatar = (user?.name || user?.email || 'U')
      .split(/[\s@.]+/).filter(Boolean).slice(0, 2)
      .map(p => p[0]?.toUpperCase()).join('') || 'U';
    return (
      <>
        <AtriumShell
          filteredItems={filteredItems}
          selected={selected}
          onSelect={selectAndReveal}
          // Sprint 137 (ADR-132) — `selected` falls back to the role default on
          // a 404/403 so the nav renders coherently; routeStatus is what tells
          // the shell that fallback isn't the page, so the title and the
          // route-change announcement stop claiming it is.
          routeStatus={routeStatus}
          role={user?.role}
          avatar={avatar}
          hasAlerts={overdueFees.length > 0}
          onBell={() => setQuickInsightsOpen(true)}
          // Sprint 90 — bell is now a real notifications panel (AtriumNotificationBell);
          // its "View all" deep-links to the Communications hub (full Notification Center).
          onViewAllNotifications={() => selectAndReveal('communications')}
          // Sprint 88A — avatar menu reuses the existing profile/logout handlers;
          // the property switcher reuses the legacy `switchTo` re-scope flow.
          onProfile={openProfile}
          onLogout={() => performLogout()}
          availableCondos={availableCondos}
          currentCondo={currentCondo}
          onSwitchCondo={switchTo}
        >
          {/* Sprint 88B (M2) — give in-shell pages (the Atrium home CTAs) a way
              to drive shell navigation without prop-drilling. `selectAndReveal`
              is the same navigator the rail/panel use. */}
          <AtriumNavProvider go={selectAndReveal}>
            {routerContent}
          </AtriumNavProvider>
        </AtriumShell>

        {/* Modals shared with the legacy shell. As of Sprint 89B the GLOBAL
            App.tsx ConfigProvider is flag-aware, so these portaled modals already
            inherit Atrium #2563EB via React context. This nested provider is kept
            as a local, explicit guarantee (belt-and-suspenders) and is harmless
            when the global theme already matches. (Sprint 86 / 89B) */}
        <ConfigProvider theme={atriumAntdTheme}>
          <ProfileModal
            open={profileOpen}
            form={profileForm}
            user={user}
            saving={profileSaving}
            onCancel={() => setProfileOpen(false)}
            onSave={handleProfileUpdate}
          />
          <LogoutConfirmModal
            open={logoutOpen}
            onCancel={() => setLogoutOpen(false)}
            onConfirm={performLogout}
          />
          <QuickInsightsModal
            open={quickInsightsOpen}
            onCancel={() => setQuickInsightsOpen(false)}
            feeCollectionRate={stats.feeCollectionRate}
            occupancyRate={stats.occupancyRate}
            totalOwners={stats.totalOwners}
          />
          <ExportReportModal
            open={exportReportOpen}
            form={exportForm}
            loading={exportLoading}
            onCancel={() => { setExportReportOpen(false); exportForm.resetFields(); }}
            onExport={handleExportReport}
          />
        </ConfigProvider>
      </>
    );
  }

  return (
    <Layout style={{ height: '100vh', overflow: 'hidden', background: '#f8fafc' }}>
      <DashboardHeader
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed(c => !c)}
        user={user as any}
        userMenuItems={userMenuItems}
        userDropdownOpen={userDropdownOpen}
        onUserDropdownOpenChange={setUserDropdownOpen}
        overdueFees={overdueFees}
        owners={owners}
        availableCondos={availableCondos as any}
        currentCondo={currentCondo as any}
        onCondoSwitch={switchTo}
      />

      <Layout style={{ height: 'calc(100vh - 72px)', overflow: 'hidden' }}>
        <DashboardSidebar
          collapsed={collapsed}
          currentCondoName={currentCondo?.name}
          summary={{ totalOwners: stats.totalOwners, feeCollectionRate: stats.feeCollectionRate }}
          menuGroups={MENU_GROUPS}
          groupColors={GROUP_COLORS}
          filteredItems={filteredItems}
          expandedGroups={expandedGroups}
          onToggleGroup={toggleGroup}
          selected={selected}
          onSelect={selectAndReveal}
        />

        <Content style={{ padding: '0 32px 32px', background: '#f8fafc', overflowY: 'auto', height: '100%' }}>
          <DashboardPageHeader
            pageTitle={pageTitle}
            isOverview={selected === 'overview'}
            currentCondoName={currentCondo?.name}
            userDisplayName={user?.name || user?.email?.split('@')[0]}
            feeCollectionRate={stats.feeCollectionRate}
            onExportReport={() => setExportReportOpen(true)}
            onQuickInsights={() => setQuickInsightsOpen(true)}
          />

          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: 32,
            minHeight: 'calc(100vh - 200px)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            border: '1px solid #e2e8f0',
          }}>
            {routerContent}
          </div>
        </Content>
      </Layout>

      <DashboardFooter />

      {/* ── Modals ──────────────────────────────────────────────────── */}
      <ProfileModal
        open={profileOpen}
        form={profileForm}
        user={user}
        saving={profileSaving}
        onCancel={() => setProfileOpen(false)}
        onSave={handleProfileUpdate}
      />
      <LogoutConfirmModal
        open={logoutOpen}
        onCancel={() => setLogoutOpen(false)}
        onConfirm={performLogout}
      />
      <QuickInsightsModal
        open={quickInsightsOpen}
        onCancel={() => setQuickInsightsOpen(false)}
        feeCollectionRate={stats.feeCollectionRate}
        occupancyRate={stats.occupancyRate}
        totalOwners={stats.totalOwners}
      />
      <ExportReportModal
        open={exportReportOpen}
        form={exportForm}
        loading={exportLoading}
        onCancel={() => { setExportReportOpen(false); exportForm.resetFields(); }}
        onExport={handleExportReport}
      />
    </Layout>
  );
};

export default MainDashboard;
